/**
 * Interactive Walkthrough System
 * Provides guided tours for different parts of the app
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, HelpCircle, 
  Sparkles, Upload, QrCode, BarChart3, Settings,
  FolderOpen, Zap, BookOpen
} from 'lucide-react';

// Tour definitions
export const TOURS = {
  beginner: {
    id: 'beginner',
    name: 'Getting Started',
    description: 'Learn the basics of uploading content',
    icon: BookOpen,
    steps: [
      {
        target: '[data-testid="description-input"]',
        title: 'Add a Title',
        description: 'Start by giving your content a title. This appears at the top when people view your tag.',
        position: 'bottom'
      },
      {
        target: '[data-testid="message-input"]',
        title: 'Write Your Message',
        description: 'Type your message here. This is the main content your viewers will see.',
        position: 'bottom'
      },
      {
        target: '[data-testid="ai-generate-button"]',
        title: 'AI Writing Assistant',
        description: 'Stuck? Click here to have AI help write your message. Just describe what you need!',
        position: 'left'
      },
      {
        target: '[data-testid="photo-gallery-button"]',
        title: 'Upload Media',
        description: 'Click here to select photos from your device. You can also upload videos and audio files using the other buttons.',
        position: 'top'
      },
      {
        target: '[data-testid="preview-button"]',
        title: 'Preview Your Content',
        description: 'Click Preview to see exactly how your content will look before saving.',
        position: 'top'
      },
      {
        target: '[data-testid="download-qr-button"]',
        title: 'Get Your QR Code',
        description: 'Generate a customizable QR code that links to your content. Perfect for printing!',
        position: 'top'
      },
      {
        target: '[data-testid="save-button"]',
        title: 'Save & Publish',
        description: 'When you\'re ready, click Save to publish your content. It\'s immediately live!',
        position: 'top'
      }
    ]
  },
  
  dashboard: {
    id: 'dashboard',
    name: 'Dashboard Overview',
    description: 'Master the admin dashboard',
    icon: BarChart3,
    steps: [
      {
        target: '[data-testid="quick-create-btn"]',
        title: 'Quick Create',
        description: 'Create a new tag instantly with one click. The upload link is automatically copied to your clipboard!',
        position: 'bottom'
      },
      {
        target: '[data-testid="tags-tab"]',
        title: 'All Tags',
        description: 'View and manage all your tags here. Use filters to find specific tags quickly.',
        position: 'bottom'
      },
      {
        target: '[data-testid="batches-tab"]',
        title: 'Batches',
        description: 'Organize tags into groups called batches. Great for managing projects or campaigns.',
        position: 'bottom'
      },
      {
        target: '[data-testid="generator-tab"]',
        title: 'UUID Generator',
        description: 'Create multiple tags at once. Set a prefix to keep them organized.',
        position: 'bottom'
      },
      {
        target: '[data-testid="search-input"]',
        title: 'Search',
        description: 'Find any tag instantly by searching codes, titles, or messages.',
        position: 'bottom'
      },
      {
        target: '[data-testid="grid-view-btn"]',
        title: 'View Options',
        description: 'Switch between grid, list, and folder views. Choose what works best for you.',
        position: 'bottom'
      }
    ]
  },
  
  tagManagement: {
    id: 'tagManagement',
    name: 'Tag Management',
    description: 'Learn to manage tags like a pro',
    icon: FolderOpen,
    steps: [
      {
        target: '[data-testid^="tag-card-"]',
        title: 'Tag Cards',
        description: 'Each card represents one tag. The thumbnail shows uploaded content. Colored dots indicate status.',
        position: 'right'
      },
      {
        target: '[data-testid^="menu-btn-"]',
        title: 'Tag Menu',
        description: 'Click the three dots to access all tag actions: view, analytics, QR code, dates, reset, and delete.',
        position: 'left'
      },
      {
        target: '[data-testid="filter-content"]',
        title: 'Filter by Status',
        description: 'Quickly filter to see only tags with content, empty tags, expired tags, or recent activity.',
        position: 'bottom'
      },
      {
        target: '[data-testid="batch-filter"]',
        title: 'Filter by Batch',
        description: 'Select a batch to view only tags in that group. Great for managing specific projects.',
        position: 'bottom'
      }
    ]
  },
  
  qrCodes: {
    id: 'qrCodes',
    name: 'QR Code Mastery',
    description: 'Create perfect QR codes',
    icon: QrCode,
    steps: [
      {
        target: '[data-testid="qr-preview"]',
        title: 'QR Preview',
        description: 'Your QR code updates in real-time as you customize. What you see is what you get!',
        position: 'right'
      },
      {
        target: '[data-testid="qr-foreground-color"]',
        title: 'Foreground Color',
        description: 'The color of the QR code dots. Use dark colors for best scanning reliability.',
        position: 'right'
      },
      {
        target: '[data-testid="qr-background-color"]',
        title: 'Background Color',
        description: 'The area behind the code. Light colors work best. Ensure good contrast!',
        position: 'right'
      },
      {
        target: '[data-testid="qr-size-selector"]',
        title: 'Size Options',
        description: 'S for digital, M for business cards, L for posters, XL for banners. Bigger = easier to scan.',
        position: 'bottom'
      },
      {
        target: '[data-testid="qr-style-selector"]',
        title: 'Dot Style',
        description: 'Square is classic, Rounded is friendly, Dots are modern. Pick what fits your brand!',
        position: 'bottom'
      },
      {
        target: '[data-testid="qr-modal-copy"]',
        title: 'Copy to Clipboard',
        description: 'Copy the QR code image directly. Paste into documents, presentations, or design tools.',
        position: 'top'
      },
      {
        target: '[data-testid="qr-modal-download"]',
        title: 'Download',
        description: 'Save as a PNG file. Ready for printing or sharing!',
        position: 'top'
      }
    ]
  },
  
  bulkOperations: {
    id: 'bulkOperations',
    name: 'Bulk Operations',
    description: 'Work with many tags at once',
    icon: Zap,
    steps: [
      {
        target: '[data-testid="generator-tab"]',
        title: 'UUID Generator',
        description: 'Create up to 100 tags at once. Use prefixes like "event-" or "product-" to stay organized.',
        position: 'bottom'
      },
      {
        target: '[data-testid="bulk-tab"]',
        title: 'CSV Upload',
        description: 'Import hundreds of tags from a spreadsheet. Download the template first!',
        position: 'bottom'
      },
      {
        target: '[data-testid="select-all-checkbox"]',
        title: 'Select Multiple',
        description: 'Check this box to select all visible tags. Then use bulk actions.',
        position: 'bottom'
      },
      {
        target: '[data-testid="quick-select-menu"]',
        title: 'Quick Select',
        description: 'Quickly select tags by status: all empty, all with content, or all in current view.',
        position: 'bottom'
      }
    ]
  }
};

// Storage key for tracking completed tours
const STORAGE_KEY = 'nfc_completed_tours';

// Get completed tours from localStorage
const getCompletedTours = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

// Mark tour as completed
const markTourCompleted = (tourId) => {
  const completed = getCompletedTours();
  if (!completed.includes(tourId)) {
    completed.push(tourId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(completed));
  }
};

// Reset all tours
export const resetAllTours = () => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Interactive Guide Component
 * Renders the step-by-step walkthrough
 */
export const InteractiveGuide = ({ tour, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);

  const steps = tour.steps;
  const step = steps[currentStep];

  // Check mobile on resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Find and highlight target element
  useEffect(() => {
    const findTarget = () => {
      const target = document.querySelector(step.target);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect(rect);
        
        // Scroll target into view if needed
        if (rect.top < 100 || rect.bottom > window.innerHeight - 100) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Update rect after scroll
          setTimeout(() => {
            setTargetRect(target.getBoundingClientRect());
          }, 500);
        }
      } else {
        setTargetRect(null);
      }
    };

    findTarget();
    const interval = setInterval(findTarget, 1000);
    return () => clearInterval(interval);
  }, [step.target, currentStep]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      markTourCompleted(tour.id);
      onComplete?.();
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  // Calculate tooltip position
  const getTooltipStyle = () => {
    if (isMobile || !targetRect) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const padding = 16;
    const tooltipWidth = 320;
    const tooltipHeight = 200;

    let style = { position: 'fixed' };

    switch (step.position) {
      case 'bottom':
        style.top = Math.min(targetRect.bottom + padding, window.innerHeight - tooltipHeight - padding);
        style.left = Math.max(padding, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - padding));
        break;
      case 'top':
        style.top = Math.max(padding, targetRect.top - tooltipHeight - padding);
        style.left = Math.max(padding, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - padding));
        break;
      case 'left':
        style.top = Math.max(padding, Math.min(targetRect.top + targetRect.height / 2 - tooltipHeight / 2, window.innerHeight - tooltipHeight - padding));
        style.left = Math.max(padding, targetRect.left - tooltipWidth - padding);
        break;
      case 'right':
        style.top = Math.max(padding, Math.min(targetRect.top + targetRect.height / 2 - tooltipHeight / 2, window.innerHeight - tooltipHeight - padding));
        style.left = Math.min(targetRect.right + padding, window.innerWidth - tooltipWidth - padding);
        break;
      default:
        style.top = '50%';
        style.left = '50%';
        style.transform = 'translate(-50%, -50%)';
    }

    return style;
  };

  const Icon = tour.icon;

  return (
    <AnimatePresence>
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] pointer-events-none"
        style={{ backgroundColor: isMobile ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.2)' }}
      >
        {/* Highlight box */}
        {!isMobile && targetRect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute border-2 border-primary rounded-lg pointer-events-none"
            style={{
              top: targetRect.top - 4,
              left: targetRect.left - 4,
              width: targetRect.width + 8,
              height: targetRect.height + 8,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.2), 0 0 20px rgba(99, 102, 241, 0.5)'
            }}
          />
        )}

        {/* Tooltip */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.9, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-background rounded-2xl shadow-2xl border border-border p-5 w-[calc(100vw-32px)] sm:w-80 pointer-events-auto"
          style={getTooltipStyle()}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon className="w-4 h-4 text-primary" />
              </div>
              <span className="text-xs font-medium text-muted-foreground">{tour.name}</span>
            </div>
            <button
              onClick={handleSkip}
              className="p-1 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <h3 className="text-base font-bold mb-2">{step.title}</h3>
          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{step.description}</p>

          {/* Progress */}
          <div className="flex items-center gap-1 mb-4">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-1 rounded-full flex-1 transition-colors ${
                  index <= currentStep ? 'bg-primary' : 'bg-secondary'
                }`}
              />
            ))}
          </div>

          {/* Step counter */}
          <p className="text-xs text-muted-foreground mb-3">
            Step {currentStep + 1} of {steps.length}
          </p>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleSkip}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip
            </button>
            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <button
                  onClick={handlePrev}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary hover:bg-secondary/80 text-sm font-medium transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                className="flex items-center gap-1 px-4 py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors"
              >
                {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
                {currentStep < steps.length - 1 && <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Help Menu Component
 * Shows available tours and lets users start them
 */
export const HelpMenu = ({ availableTours = ['beginner', 'dashboard', 'tagManagement', 'qrCodes', 'bulkOperations'] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTour, setActiveTour] = useState(null);
  const [completedTours, setCompletedTours] = useState([]);

  useEffect(() => {
    setCompletedTours(getCompletedTours());
  }, [isOpen]);

  const startTour = (tourId) => {
    setActiveTour(TOURS[tourId]);
    setIsOpen(false);
  };

  const handleTourComplete = () => {
    setCompletedTours(getCompletedTours());
    setActiveTour(null);
  };

  const tours = availableTours.map(id => TOURS[id]).filter(Boolean);

  return (
    <>
      {/* Help Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(!isOpen)}
          className="w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl flex items-center justify-center transition-shadow"
          data-testid="help-menu-button"
        >
          <HelpCircle className="w-6 h-6" />
        </motion.button>

        {/* Menu Popup */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              className="absolute bottom-16 right-0 w-72 bg-background rounded-2xl shadow-2xl border border-border overflow-hidden"
            >
              {/* Header */}
              <div className="p-4 border-b border-border bg-secondary/30">
                <h3 className="font-bold text-lg">Need Help?</h3>
                <p className="text-sm text-muted-foreground">Choose a guided tour</p>
              </div>

              {/* Tour List */}
              <div className="p-2 max-h-80 overflow-y-auto">
                {tours.map((tour) => {
                  const Icon = tour.icon;
                  const isCompleted = completedTours.includes(tour.id);
                  
                  return (
                    <button
                      key={tour.id}
                      onClick={() => startTour(tour.id)}
                      className="w-full p-3 rounded-xl hover:bg-secondary/50 transition-colors text-left flex items-start gap-3 group"
                      data-testid={`tour-${tour.id}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                        isCompleted ? 'bg-green-100 text-green-600' : 'bg-primary/10 text-primary'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{tour.name}</span>
                          {isCompleted && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              Done
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{tour.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {tour.steps.length} steps
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0 mt-1" />
                    </button>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="p-3 border-t border-border bg-secondary/20">
                <button
                  onClick={() => {
                    resetAllTours();
                    setCompletedTours([]);
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Reset all tours
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Active Tour */}
      {activeTour && (
        <InteractiveGuide
          tour={activeTour}
          onClose={() => setActiveTour(null)}
          onComplete={handleTourComplete}
        />
      )}
    </>
  );
};

export default HelpMenu;
